import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import Tabs from '_lex/_atoms/Tabs';
import parse, { DOMNode } from 'html-react-parser';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import useAccreditationStandardById from '../../../_data_sources/AccreditationStandards/useStandard';
import { translate as t } from 'hooks/useTranslations';
import { useStandardsContext } from '../context';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import { neutral } from '_lex/_foundations/phxColors';
import noStandardSelected from '../../images/no_standard_selected.svg';
import StandardsList from '../List';
import EditorSheet from '_lex/_atoms/EditorSheet';
import WrittenDirectivesSummary from './WrittenDirectivesSummary';
import ComplianceChecklistTabContent from './ComplianceChecklistTabContent';
import { Jurisdiction } from 'types/jurisdiction';
import { AppContext } from 'views/App/context';
import ActionMenu, { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import usePortfolioExportFullPolicies from '../../../_data_sources/PortfolioExports/usePortfolioExportFullPolicies';
import usePortfolioExportTaggedDirectives from '../../../_data_sources/PortfolioExports/usePortfolioExportTaggedDirectives';
import useGenerateCrossReferenceReport from '../../../_data_sources/Reports/useGenerateCrossReferenceReport';
import useComplianceReportExport from '../../../_data_sources/ComplianceReport/useComplianceReportExport';
import { enqueueSnackbar } from 'notistack';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import useEnabledVersionsInfo from '../../../_hooks/useEnabledVersionsInfo';

const TAB_STANDARD = 'standard';
const TAB_COMPLIANCE_CHECKLIST = 'compliance-checklist';

const StandardDetail: React.FC = () => {
  const { id: accreditationId, versionId, standardId } = useParams();
  const { getStandardType, isLoading: standardsLoading, version } = useStandardsContext();
  const { currentOrganizationId, isFeatureEnabled, hasPermission } = useContext(AppContext);
  const hasComplianceTab = hasPermission('awbPremium', currentOrganizationId) || isFeatureEnabled('awbPremiumDemo');

  const [contentTab, setContentTab] = useState<string>(TAB_STANDARD);

  const versionsInfo = useEnabledVersionsInfo(currentOrganizationId || '');

  const backUrl = useMemo(() => {
    if (versionsInfo.isLoading) {
      return `/admin/accreditation/${accreditationId}/versions/`;
    }

    const { hasSingleBody, hasSingleEnabledVersion } = versionsInfo;

    if (hasSingleBody && hasSingleEnabledVersion) {
      return '/admin/accreditation';
    }

    if (hasSingleBody) {
      return `/admin/accreditation/${accreditationId}/versions/`;
    }

    return '/admin/accreditation';
  }, [versionsInfo, accreditationId]);

  const standardByIdQuery = useAccreditationStandardById(
    currentOrganizationId,
    accreditationId!,
    versionId!,
    standardId || '',
  );

  const isLoading = standardByIdQuery.isLoading || standardByIdQuery.isFetching || standardsLoading;

  const [standardType, setStandardType] = useState<'standard' | 'subStandard' | 'requirement' | undefined>(undefined);
  const [standardTypeText, setStandardTypeText] = useState<string>('');

  const exportFullPoliciesMutation = usePortfolioExportFullPolicies(
    () => {
      enqueueSnackbar(t('awb.export.success'), { variant: 'success' });
    },
    (error: ServerErrorResponse) => {
      errorHelper.enqueueErrors(error);
    },
  );

  const exportTaggedDirectivesMutation = usePortfolioExportTaggedDirectives(
    () => {
      enqueueSnackbar(t('awb.export.success'), { variant: 'success' });
    },
    (error: ServerErrorResponse) => {
      errorHelper.enqueueErrors(error);
    },
  );

  const complianceReportMutation = useComplianceReportExport(
    () => {
      enqueueSnackbar(t('awb.export.success'), { variant: 'success' });
    },
    (error: ServerErrorResponse) => {
      errorHelper.enqueueErrors(error);
    },
  );

  const crossReferenceReportMutation = useGenerateCrossReferenceReport(
    () => {
      enqueueSnackbar(t('awb.export.success'), { variant: 'success' });
    },
    (error: any) => {
      errorHelper.enqueueErrors(error as ServerErrorResponse);
    },
  );

  const handleExportFullPolicies = () => {
    if (!currentOrganizationId || !accreditationId || !versionId) {
      console.error('Missing required parameters for export');
      return;
    }

    exportFullPoliciesMutation.mutate({
      orgId: currentOrganizationId,
      accreditationBodyId: accreditationId,
      accreditationBodyVersionId: versionId,
    });
  };

  const handleExportTaggedDirectives = () => {
    if (!currentOrganizationId || !accreditationId || !versionId) {
      console.error('Missing required parameters for export');
      return;
    }

    exportTaggedDirectivesMutation.mutate({
      orgId: currentOrganizationId,
      accreditationBodyId: accreditationId,
      accreditationBodyVersionId: versionId,
    });
  };

  const handleGenerateCrossReferenceReport = () => {
    if (!currentOrganizationId || !accreditationId || !versionId) {
      return;
    }

    crossReferenceReportMutation.mutate({
      orgId: currentOrganizationId,
      accreditationBodyId: accreditationId,
      accreditationBodyVersionId: versionId,
    });
  };

  const handleExportComplianceReport = () => {
    if (!currentOrganizationId || !accreditationId || !versionId) {
      console.error('Missing required parameters for compliance report export');
      return;
    }

    complianceReportMutation.mutate({
      orgId: currentOrganizationId,
      accreditationBodyId: accreditationId,
      accreditationBodyVersionId: versionId,
    });
  };

  const setExportMenuItems = () => {
    const actions: ActionMenuItemProps[] = [];

    actions.push({
      key: 'exportAssessmentPortfolioFull',
      label: t('awb.export.assessmentPortfolioFull'),
      onClick: handleExportFullPolicies,
      disabled: exportFullPoliciesMutation.isLoading,
    });

    actions.push({
      key: 'exportAssessmentPortfolioTagged',
      label: t('awb.export.assessmentPortfolioTagged'),
      onClick: handleExportTaggedDirectives,
      disabled: exportTaggedDirectivesMutation.isLoading,
    });

    if (hasComplianceTab) {
      actions.push({
        key: 'exportComplianceReport',
        label: t('awb.export.complianceReport'),
        onClick: handleExportComplianceReport,
        disabled: complianceReportMutation.isLoading,
      });

      actions.push({
        key: 'exportCrossReferenceReport',
        label: t('awb.export.crossReferenceReport'),
        onClick: handleGenerateCrossReferenceReport,
        disabled: crossReferenceReportMutation.isLoading,
      });
    }

    return actions;
  };

  useEffect(() => {
    if (!standardId || !version || standardsLoading) {
      setStandardType('standard');
      setStandardTypeText(t('awb.standards.new.standard'));
      return;
    }

    const type = getStandardType(standardId);
    setStandardType(type);

    switch (type) {
      case 'standard':
        setStandardTypeText(t('awb.standards.new.standard'));
        break;
      case 'subStandard':
        setStandardTypeText(t('awb.standards.new.subStandard'));
        break;
      case 'requirement':
        setStandardTypeText(t('awb.standards.new.requirement'));
        break;
      default:
        setStandardTypeText(t('awb.standards.new.standard'));
    }
  }, [standardId, getStandardType, version, standardsLoading, t]);

  const renderContent = () => {
    if (!standardId) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            itemsAlign: 'center',
            textAlign: 'center',
            minHeight: '60vh',
          }}
        >
          <img
            src={`${noStandardSelected}`}
            alt={'Select a standard image.'}
            style={{ height: '240px' }}
          />
          <Typography sx={{ color: neutral[500] }}>{t('awb.accreditationStandards.emptyScreen')}</Typography>
        </Box>
      );
    }

    return (
      <EditorSheet sx={hasComplianceTab ? { gridTemplateRows: 'auto 1fr' } : undefined}>
        {hasComplianceTab && (
          <Box>
            <Tabs
              value={contentTab}
              onChange={(_, v: string | number) => setContentTab(v as string)}
              items={[
                { label: standardTypeText || t('awb.standards.new.standard'), value: TAB_STANDARD },
                { label: t('awb.complianceChecklist.title'), value: TAB_COMPLIANCE_CHECKLIST },
              ]}
            />
          </Box>
        )}

        {contentTab === TAB_STANDARD &&
          (isLoading ? (
            <CircularProgress />
          ) : (
            <Box sx={{ px: hasComplianceTab ? 5 : 0, py: hasComplianceTab ? 3 : 0 }}>
              <Stack spacing={3}>
                <Typography variant='eyebrowMd'>{standardTypeText}</Typography>
                <Typography variant='subtitleMd'>{`${standardByIdQuery.data?.heading} ${standardByIdQuery.data?.title}`}</Typography>
                <Box sx={{ py: 3, minHeight: 0, overflow: 'auto' }}>
                  {parse(standardByIdQuery.data?.publishedContent ?? '', {
                    replace(node: DOMNode, ind: number) {
                      return PolicyContentHelper.handleContent(node, ind);
                    },
                  })}
                </Box>
              </Stack>
            </Box>
          ))}

        {hasComplianceTab && contentTab === TAB_COMPLIANCE_CHECKLIST && (
          <ComplianceChecklistTabContent standardId={standardId} />
        )}
      </EditorSheet>
    );
  };

  return (
    <TakeoverLayout
      pageTitle={version?.version || ''}
      backUrl={backUrl}
      leftDrawer={<StandardsList />}
      contentBackground='neutral'
      rightDrawer={
        standardId &&
        version && (
          <WrittenDirectivesSummary
            standardId={standardId}
            jurisdictions={
              Array.isArray(version?.jurisdictions) && typeof version?.jurisdictions[0] === 'object'
                ? (version.jurisdictions as Jurisdiction[])
                : []
            }
          />
        )
      }
      menubarLeft={
        <ActionMenu
          id='export_actions'
          btnText={t('awb.export.generateReport')}
          items={setExportMenuItems()}
          btnProps={{
            size: 'small',
            variant: 'outlined',
            color: 'neutral',
            disabled:
              exportFullPoliciesMutation.isLoading ||
              exportTaggedDirectivesMutation.isLoading ||
              complianceReportMutation.isLoading ||
              crossReferenceReportMutation.isLoading,
            loading:
              exportFullPoliciesMutation.isLoading ||
              exportTaggedDirectivesMutation.isLoading ||
              complianceReportMutation.isLoading ||
              crossReferenceReportMutation.isLoading,
          }}
        />
      }
    >
      {renderContent()}
    </TakeoverLayout>
  );
};

export default StandardDetail;
